import { useState } from 'react';
import { ErrorApi } from '../../api/problema';
import { Chip } from '../../componentes/Chip';
import { EstadoError, EstadoSinPermiso } from '../../componentes/EstadoPagina';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { PanelLateral } from '../../componentes/PanelLateral';
import { Segmentos } from '../../componentes/Segmentos';
import { rolesDe } from '../../logica/acceso';
import { dinero } from '../../logica/dinero';
import { avisoBreveDeError, estadoDeError } from '../../logica/estados';
import { haySiguiente, usePaginacionCursor } from '../../logica/paginacion';
import { usePermisos } from '../../permisos/permisos';
import type { PropsPantalla } from '../tipos';
import { fechaCorta, opsDelPortal, useCarga } from './carga';
import { BASE_COMISION, ESTADO_COMISION, lineaVenta, PERMISOS_PORTAL, periodoLiquidacion, unidadVenta } from './logica';
import type { DatosPortal } from './PortalRrpp';

/**
 * Ventas (lo atribuido con su `reference` y `label`, G507, G788; sin datos del comprador) y Comisiones · Liquidaciones
 * (solo las cerradas, G790; solo consulta, G8). Ningún importe se calcula en el panel: cada fila con el suyo de la API.
 */
export function Ganancias({ props, datos, seccion }: { props: PropsPantalla; datos: DatosPortal; seccion: 'ventas' | 'comisiones' }) {
    const { busqueda, navegar, ruta, movil, membresia, nombreNegocio } = props;
    const permisos = usePermisos();
    const vista = busqueda.get('vista') === 'liquidaciones' ? 'liquidaciones' : 'comisiones';
    const sinPermiso = (s: string) => <EstadoSinPermiso seccion={s} rol={membresia === undefined ? '' : rolesDe(membresia)} negocio={nombreNegocio} onInicio={() => { navegar('/'); }} movil={movil} />;

    if (seccion === 'ventas') {
        return permisos.puede(PERMISOS_PORTAL.comisiones) ? <Ventas props={props} datos={datos} /> : sinPermiso('Ventas');
    }
    const items = [
        ...(permisos.puede(PERMISOS_PORTAL.comisiones) ? [{ valor: 'comisiones', etiqueta: 'Comisiones', icono: 'percent' as const }] : []),
        ...(permisos.puede(PERMISOS_PORTAL.liquidaciones) ? [{ valor: 'liquidaciones', etiqueta: 'Liquidaciones', icono: 'wallet' as const }] : []),
    ];
    const activa = items.find((i) => i.valor === vista) ?? items[0];
    if (activa === undefined) {
        return sinPermiso('Comisiones');
    }
    return (
        <div className="rv-ca-apartado">
            {items.length > 1 && (
                <Segmentos
                    etiqueta="Comisiones y liquidaciones"
                    movil={movil}
                    items={items}
                    valor={items.indexOf(activa)}
                    onCambio={(i) => { navegar(`${ruta}?seccion=comisiones&vista=${items[i]?.valor ?? 'comisiones'}`, true); }}
                />
            )}
            {activa.valor === 'comisiones' ? <Comisiones props={props} datos={datos} /> : <Liquidaciones props={props} />}
        </div>
    );
}

function Ventas({ props, datos }: { props: PropsPantalla; datos: DatosPortal }) {
    const { negocio, movil } = props;
    const pag = usePaginacionCursor();
    const carga = useCarga(`ventas|${negocio}|${pag.cursor ?? ''}`, () => datos.ops.listPromoterAttributions(negocio, { cursor: pag.cursor ?? undefined, limit: 8 }));
    const p = carga.datos;
    if (carga.error !== null && p === null) {
        return <EstadoError seccion="tus ventas" codigoSoporte={estadoDeError(carga.error).codigoSoporte} onReintentar={carga.recargar} movil={movil} />;
    }
    if (p === null) {
        return <div className="rv-rp-esqueleto" aria-busy="true" />;
    }
    if (p.items.length === 0) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="ticket" /></span>
                <strong>Aún no tienes ventas</strong>
                <span>Cuando alguien compre con tu enlace o tu código, lo verás aquí.</span>
            </div>
        );
    }
    return (
        <div className="rv-ca-apartado">
            <div className="rv-ca-lista" aria-label="Tus ventas">
                {p.items.map((v) => (
                    <div key={`${v.source_kind}-${v.source_id}`} className="rv-ca-fila-caja">
                        <div className="rv-ca-fila">
                            <span className="rv-rp-cantidad"><strong>{v.quantity}</strong><small>{unidadVenta(v)}</small></span>
                            <span className="rv-ca-fila__texto">
                                <strong>{datos.nombreEvento(v.event_id)}</strong>
                                <span>{[lineaVenta(v), fechaCorta(v.created_at)].filter((x) => x !== '').join(' · ')}</span>
                            </span>
                            {v.status === 'cancelled' && <Chip estado="problem">Cancelada</Chip>}
                            {v.sale_amount != null && (v.status === 'cancelled' ? <s>{dinero(v.sale_amount)}</s> : <strong>{dinero(v.sale_amount)}</strong>)}
                        </div>
                    </div>
                ))}
            </div>
            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(p.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(p.next_cursor); }} movil={movil} />
        </div>
    );
}

function Comisiones({ props, datos }: { props: PropsPantalla; datos: DatosPortal }) {
    const { negocio, movil } = props;
    const pag = usePaginacionCursor();
    const carga = useCarga(`comisiones|${negocio}|${pag.cursor ?? ''}`, () => datos.ops.listCommissions(negocio, { cursor: pag.cursor ?? undefined, limit: 20 }));
    const p = carga.datos;
    if (carga.error !== null && p === null) {
        return <EstadoError seccion="tus comisiones" codigoSoporte={estadoDeError(carga.error).codigoSoporte} onReintentar={carga.recargar} movil={movil} />;
    }
    if (p === null) {
        return <div className="rv-rp-esqueleto" aria-busy="true" />;
    }
    if (p.data.length === 0) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="percent" /></span>
                <strong>Aún no tienes comisiones</strong>
                <span>Se calculan cuando acaba cada noche, con tus invitados y tus ventas.</span>
            </div>
        );
    }
    return (
        <>
            <div className="rv-ca-lista" aria-label="Tus comisiones">
                {p.data.map((c) => {
                    const [texto, chip] = ESTADO_COMISION[c.status];
                    return (
                        <div key={c.id} className="rv-ca-fila-caja">
                            <div className="rv-ca-fila">
                                <span className="rv-ca-fila__texto">
                                    <strong>{datos.nombreEvento(c.event_id)}</strong>
                                    <span>{[BASE_COMISION[c.basis], fechaCorta(c.created_at)].filter((x) => x !== '').join(' · ')}</span>
                                </span>
                                <Chip estado={chip}>{texto}</Chip>
                                <strong>{dinero(c.amount)}</strong>
                            </div>
                        </div>
                    );
                })}
            </div>
            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(p.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(p.next_cursor); }} movil={movil} />
            <p className="rv-ca-nota rv-ca-nota--gris"><Icono nombre="info" /><span>«Estimada»: la noche aún no ha terminado y puede cambiar con el recuento final.</span></p>
        </>
    );
}

function Liquidaciones({ props }: { props: PropsPantalla }) {
    const { negocio, movil } = props;
    const ops = opsDelPortal(props.ops);
    const pag = usePaginacionCursor();
    const [abierta, setAbierta] = useState<string | null>(null);
    // G790: el portal solo pide las cerradas.
    const carga = useCarga(`liquidaciones|${negocio}|${pag.cursor ?? ''}`, () => ops.listSettlements(negocio, { status: 'closed', cursor: pag.cursor ?? undefined, limit: 20 }));
    const ficha = useCarga(abierta === null ? null : `liquidacion|${abierta}`, () => ops.getSettlement(negocio, abierta ?? ''));
    const p = carga.datos;
    if (carga.error !== null && p === null) {
        return <EstadoError seccion="tus liquidaciones" codigoSoporte={estadoDeError(carga.error).codigoSoporte} onReintentar={carga.recargar} movil={movil} />;
    }
    if (p === null) {
        return <div className="rv-rp-esqueleto" aria-busy="true" />;
    }
    if (p.data.length === 0) {
        return (
            <div className="rv-ca-vacia">
                <span className="rv-ca-vacia__baldosa"><Icono nombre="wallet" /></span>
                <strong>Aún no tienes liquidaciones cerradas</strong>
                <span>Cuando el negocio cierre una liquidación tuya, te avisamos y la verás aquí.</span>
            </div>
        );
    }
    const l = ficha.datos;
    return (
        <>
            {/* G834: las sumas de todas tus liquidaciones cerradas, de la API. */}
            {p.totals !== undefined && (
                <div className="rv-ca-dato"><span>{`Liquidado en total · pagado ${dinero(p.totals.paid_total)}`}</span><strong>{dinero(p.totals.total)}</strong></div>
            )}
            <div className="rv-ca-lista" aria-label="Tus liquidaciones">
                {p.data.map((s) => (
                    <div key={s.id} className="rv-ca-fila-caja">
                        <div className="rv-ca-fila rv-ca-fila--abre" role="button" tabIndex={0} onClick={() => { setAbierta(s.id); }} onKeyDown={(e) => { if (e.key === 'Enter') { setAbierta(s.id); } }}>
                            <span className="rv-ca-fila__texto">
                                <strong>{periodoLiquidacion(s)}</strong>
                                <span>{`Pagado ${dinero(s.paid_total)}`}</span>
                            </span>
                            <strong>{dinero(s.total)}</strong>
                            <Icono nombre="chevron-right" className="rv-ca-chevron" />
                        </div>
                    </div>
                ))}
            </div>
            <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={haySiguiente(p.next_cursor)} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(p.next_cursor); }} movil={movil} />
            {abierta !== null && (
                <PanelLateral abierto onCerrar={() => { setAbierta(null); }} movil={movil} tamano="ficha" titulo={l === null ? 'Liquidación' : periodoLiquidacion(l)} eyebrow="Liquidación cerrada">
                    {ficha.error !== null && l === null ? (
                        <p className="rv-ca-nota"><Icono nombre="circle-alert" /><span>{ficha.error instanceof ErrorApi && ficha.error.estado === 404 ? 'Esta liquidación ya no está disponible.' : (avisoBreveDeError(ficha.error) ?? 'No hemos podido cargarla.')}</span></p>
                    ) : l === null ? (
                        <div className="rv-rp-esqueleto" aria-busy="true" />
                    ) : (
                        <div className="rv-ca-cuerpo">
                            <div>
                                <div className="rv-ca-dato"><span>Comisiones</span><strong>{dinero(l.commissions_total)}</strong></div>
                                <div className="rv-ca-dato"><span>Ajustes</span><strong>{dinero(l.adjustments_total)}</strong></div>
                                <div className="rv-ca-dato"><span>Total</span><strong>{dinero(l.total)}</strong></div>
                                <div className="rv-ca-dato"><span>Pagado</span><strong>{dinero(l.paid_total)}</strong></div>
                            </div>
                            {(l.adjustments ?? []).length > 0 && (
                                <div className="rv-ca-bloque">
                                    <div className="rv-ca-bloque__cab"><strong>Ajustes</strong></div>
                                    {(l.adjustments ?? []).map((a, i) => (
                                        <div key={`${a.created_at ?? ''}${String(i)}`} className="rv-ca-dato"><span>{a.reason}</span><strong>{dinero(a.amount)}</strong></div>
                                    ))}
                                </div>
                            )}
                            {(l.payments ?? []).length > 0 && (
                                <div className="rv-ca-bloque">
                                    <div className="rv-ca-bloque__cab"><strong>Pagos</strong></div>
                                    {(l.payments ?? []).map((x, i) => (
                                        <div key={x.id ?? String(i)} className="rv-ca-dato"><span>{fechaCorta(x.paid_at)}</span><strong>{dinero(x.amount)}</strong></div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </PanelLateral>
            )}
        </>
    );
}
